/* eslint-disable react/forbid-prop-types */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { exchange, currencies } from '../../../utilities/constants';
import { formatNumber, formatNumberWithComma } from '../../../utilities/formatHelpers';
import { BANK_REQUESTS_COINS } from '../../Accounts/constants';
import { USER_PREFERENCES } from '../../../utilities/sharedQueries';
import { useRate } from '../../../utilities/sharedHooks/rates';
import { useUserCardProvider } from '../../../utilities/sharedHooks/user';
import { cardProviders } from '../constants';

const CardCryptodraftDetails = ({ details, cardStatus = null, ...props }) => {
  const { t: translate } = useTranslation();

  const { data: userPref } = useQuery(USER_PREFERENCES);
  const { cardProvider } = useUserCardProvider();

  const fiatCurrency = useMemo(() => {
    if (cardProvider === cardProviders.MONAVATE && cardStatus?.displayCurrency) {
      return cardStatus?.displayCurrency;
    }
    return userPref?.displayCurrency;
  }, [cardProvider, cardStatus, userPref?.displayCurrency]);

  const principalCurrency = useMemo(() => {
    if (fiatCurrency?.toLowerCase() === BANK_REQUESTS_COINS.EUR) {
      return BANK_REQUESTS_COINS.EUROC;
    }
    if (
      fiatCurrency?.toLowerCase() === BANK_REQUESTS_COINS.USD ||
      (fiatCurrency?.toLowerCase() === BANK_REQUESTS_COINS.GBP &&
        cardProvider === cardProviders.MONAVATE)
    ) {
      return BANK_REQUESTS_COINS.USDT;
    }
    return fiatCurrency;
  }, [fiatCurrency, cardProvider]);

  const cryptodraftPrincipleToDispalyCurrencyRate = useRate(
    details?.principal_currency,
    principalCurrency
  );

  const spendingLimit =
    details.spending_limit *
    currencies[details.principal_currency.toUpperCase()].conversion *
    cryptodraftPrincipleToDispalyCurrencyRate;

  const principleNetAmount =
    details.principal_net_amount *
    currencies[details.principal_currency.toUpperCase()].conversion *
    cryptodraftPrincipleToDispalyCurrencyRate;

  return (
    <div
      role="button"
      tabIndex="0"
      onClick={props.openModal}
      onKeyPress={props.openModal}
      className="bg-lighter ps-3 pb-3 pt-4 rounded-extra d-flex flex-column w-100"
      style={{
        marginTop: '-1rem',
        paddingRight: '0.563rem',
      }}>
      <div
        className="d-flex flex-row mb-3 justify-content-between align-items-center"
        style={{ marginTop: '0.25rem' }}>
        <p className="text-f5 text-bold" style={{ letterSpacing: '0.48px' }}>
          {translate('cryptodraft_promotional_title')}
        </p>

        <button
          type="button"
          className="btn btn-primary rounded-4 py-2 d-flex align-items-center"
          onClick={props.openModal}>
          <p className="text-f6 text-bold mx-2 my-0">{translate('manage')}</p>
        </button>
      </div>
      <div className="pe-2">
        <div className="d-flex flex-row justify-content-between" style={{ marginTop: '0.25rem' }}>
          <p className="text-f6 text-shadow">
            {translate('cryptodraft_details_applicable_interest')}
          </p>
          <p className="text-f6 text-dark">{`${details.mpr_interest}%`}</p>
        </div>
        <div className="d-flex flex-row justify-content-between mt-2">
          <p className="text-f6 text-shadow">{translate('cryptodraft_details_current_ltv')}</p>
          <p className="text-f6 text-dark">{`${details.ltv_percentage}%`}</p>
        </div>
        <div className="progress mt-3 bg-disabled">
          <div
            className="progress-bar bg-white "
            style={{
              width: `${(details.principal_net_amount / details.spending_limit) * 100}%`,
            }}
          />
        </div>
        <div className="d-flex flex-row justify-content-between align-items-center mt-3">
          <p className="text-f6 text-shadow">
            <img src="./assets/images/locked.svg" alt="locked" className="me-1 pb-1 text-shadow" />
            {translate('cryptodraft_details_limit_spent')}
          </p>
          <p className="text-f6 text-dark">
            {`${formatNumberWithComma(formatNumber(principleNetAmount, exchange.FIAT_VALUE))} / `}
            <span className="text-shadow">{`${formatNumberWithComma(
              formatNumber(spendingLimit, exchange.FIAT_VALUE)
            )} ${principalCurrency?.toUpperCase()}`}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CardCryptodraftDetails;

CardCryptodraftDetails.propTypes = {
  openModal: PropTypes.func.isRequired,
  details: PropTypes.object.isRequired,
  cardStatus: PropTypes.object,
};
